import React from 'react';
import { PhoneCall, ClipboardList, Stethoscope, CalendarCheck, FileText, Pill, Image, NotebookPen } from 'lucide-react';
import { Reveal } from './Shared';
import { CLINIC } from '../mock';
import { useLang } from '../context/LangContext';

const STEPS = [
  { icon: PhoneCall, hiTitle: 'कॉल या व्हाट्सऐप से बुक करें', hiBody: 'अपने नज़दीकी क्लिनिक चुनें: गाज़ियाबाद, आगरा, मुरादाबाद या अलीगढ़।', title: 'Book by call or WhatsApp', body: 'Choose the clinic nearest to you: Ghaziabad, Agra, Moradabad or Aligarh.' },
  { icon: ClipboardList, hiTitle: 'विस्तार से जानकारी', hiBody: 'डॉ. पुनीत आपकी पूरी बात सुनते हैं: लक्षण, परिवार का इतिहास और पहले का इलाज।', title: 'Detailed history', body: 'Dr Puneet listens to your full story: symptoms, family history and previous treatment.' },
  { icon: Stethoscope, hiTitle: 'पूरी जाँच', hiBody: 'जोड़ों, रीढ़ और त्वचा की सावधानी से जाँच, ज़रूरत हो तो टेस्ट या MSK अल्ट्रासाउंड।', title: 'Thorough examination', body: 'A careful joint, spine and skin examination, with targeted tests or MSK ultrasound if needed.' },
  { icon: CalendarCheck, hiTitle: 'साफ़ योजना और फ़ॉलो-अप', hiBody: 'आप जानकर जाते हैं कि संभावित बीमारी क्या है, इलाज क्या है और दोबारा कब आना है।', title: 'Clear plan & follow-up', body: 'You leave knowing your likely diagnosis, your treatment plan and when to come back.' },
];

const BRING = [
  { icon: FileText, hi: 'पुरानी ब्लड टेस्ट रिपोर्ट (ANA, RA फ़ैक्टर, CRP, ESR, यूरिक एसिड आदि)', label: 'Previous blood test reports (ANA, RA factor, CRP, ESR, uric acid, etc.)' },
  { icon: Image, hi: 'एक्स-रे, MRI या स्कैन की रिपोर्ट और फ़िल्में', label: 'X-rays, MRI or scan reports and films' },
  { icon: Pill, hi: 'सभी चल रही दवाइयाँ, या उनके पत्तों की फ़ोटो', label: 'All current medicines, or photos of the strips' },
  { icon: NotebookPen, hi: 'लक्षणों और वे कब शुरू हुए, इसका छोटा सा नोट', label: 'A short note of your symptoms and when they started' },
];

export const FirstVisit = () => {
  const { tr } = useLang();
  return (
  <div data-testid="first-visit-guide" className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start">
    <ol className="relative space-y-5">
      {STEPS.map((s, i) => (
        <Reveal key={s.title} delay={i * 70}>
          <li data-testid={`first-visit-step-${i + 1}`} className="flex gap-5 bg-white rounded-2xl border border-teal-50 p-5 shadow-sm">
            <span className="relative grid place-items-center w-12 h-12 rounded-xl text-white shrink-0" style={{ background: 'var(--teal-700)' }}>
              <s.icon size={22} />
              <span className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full text-[11px] font-bold text-white" style={{ background: 'var(--coral)' }}>{i + 1}</span>
            </span>
            <div><h3 className="font-semibold text-teal-900">{tr(s.title, s.hiTitle)}</h3><p className="text-sm text-slate-600 mt-1 leading-relaxed">{tr(s.body, s.hiBody)}</p></div>
          </li>
        </Reveal>
      ))}
    </ol>
    <Reveal delay={120}>
      <div className="rounded-3xl p-7 text-white" style={{ background: 'linear-gradient(150deg,#0b3b3a,#0f766e)' }}>
        <h3 className="font-display text-2xl" style={{ fontWeight: 600 }}>{tr('What to bring', 'साथ क्या लाएँ')}</h3>
        <p className="text-teal-100/90 text-sm mt-2">{tr('Bringing these helps us avoid repeat tests and reach a diagnosis faster.', 'इन्हें लाने से दोबारा टेस्ट नहीं कराने पड़ते और निदान जल्दी होता है।')}</p>
        <ul className="mt-6 space-y-4">
          {BRING.map((b) => (
            <li key={b.label} className="flex gap-3 text-sm"><span className="grid place-items-center w-9 h-9 rounded-lg bg-white/10 shrink-0"><b.icon size={17} className="text-amber-200" /></span><span className="pt-1.5">{tr(b.label, b.hi)}</span></li>
          ))}
        </ul>
        <div data-testid="first-visit-fee" className="mt-7 rounded-2xl bg-white/10 border border-white/15 p-5">
          <div className="text-xs uppercase tracking-wider text-teal-100">{tr('Consultation fee', 'परामर्श शुल्क')}</div>
          <div className="font-display text-3xl mt-1" style={{ fontWeight: 600 }}>{CLINIC.fee}</div>
          <p className="text-sm text-teal-100/90 mt-1">{tr('Same fee for in-clinic and online video consultations.', 'क्लिनिक और ऑनलाइन वीडियो परामर्श, दोनों की फ़ीस एक ही है।')}</p>
        </div>
      </div>
    </Reveal>
  </div>
  );
};
